type ChatBotContext = {
  chatBotId?: string;
  learnerTrainingId?: string;
  chapterSequenceId?: string | number;
  conversationName?: string;
  textLocale?: string;
};

export interface ConversationResponse {
  error?: boolean;
  message?: string;
  conversation_id?: string;
}

export interface MessageResponse {
  error?: boolean;
  message?: string;
  text?: string;
}

export type ResetResponse = ConversationResponse;

export class ChatBotService {
  private baseUrl = `${process.env.REACT_APP_BACKEND_API_BASE_URL}/api`;
  private token: string;
  private context: Record<string, string | number | undefined>;
  private textLocale: string;

  async sessionStorageKey(): Promise<string | null> {
    if (
      !this.token ||
      !this.context.learner_training_id ||
      !this.context.chapter_sequence_id ||
      !this.context.chatbot_id
    )
      return null;
    try {
      const identity = JSON.stringify([
        this.token,
        this.context.learner_training_id,
        this.context.chapter_sequence_id,
        this.context.chatbot_id,
        this.textLocale
      ]);
      const digest = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(identity)
      );
      return `roleplay-v1:${Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')}`;
    } catch {
      return null;
    }
  }

  constructor(ctx: ChatBotContext = {}) {
    this.textLocale = ctx.textLocale?.toLowerCase() || 'en';
    this.token = window.location.pathname.split('/')[2] || '';
    try {
      this.token = sessionStorage.getItem('token') || this.token;
    } catch {
      // Embedded browsers can deny storage while URL authentication still works.
    }
    this.context = {
      chatbot_id: ctx.chatBotId,
      learner_training_id: ctx.learnerTrainingId,
      chapter_sequence_id: ctx.chapterSequenceId,
      conversation_name: ctx.conversationName
    };
  }

  private async post<T>(
    path: string,
    payload: Record<string, unknown> = {}
  ): Promise<T & { error?: boolean; message?: string }> {
    const controller = new AbortController();
    // Legacy replies can use the server's 120-second provider timeout.
    // Allow headroom to receive that result before declaring it unconfirmed.
    const timeout = setTimeout(() => controller.abort(), 150_000);
    try {
      const response = await fetch(`${this.baseUrl}/chatbots/${path}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...this.context, ...payload }),
        signal: controller.signal
      });
      // Provider diagnostics can contain private output. Keep them out of UI state.
      if (!response.ok)
        return { error: true, message: `HTTP ${response.status}` } as T & {
          error: boolean;
          message: string;
        };
      return await response.json();
    } catch {
      return { error: true, message: 'Reply could not be confirmed.' } as T & {
        error: boolean;
        message: string;
      };
    } finally {
      clearTimeout(timeout);
    }
  }

  createChatbotConversation(): Promise<ConversationResponse> {
    return this.post<ConversationResponse>('conversations');
  }

  sendChatbotMessage(
    convId: string,
    text: string,
    subject?: string
  ): Promise<MessageResponse> {
    return this.post<MessageResponse>('messages', {
      conversation_id: convId,
      text,
      bot: 'roleplay_assessor',
      subject
    });
  }

  resetChatbotConversation(): Promise<ResetResponse> {
    return this.post<ResetResponse>('reset');
  }
}
